import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Sparkles, Wallet, BookOpen, Map, Send, Check, ArrowRight,
  Trophy, GraduationCap, Landmark, Globe, ScrollText, FileText,
} from "lucide-react";
import PlusReportBuyCard from "@/components/app/PlusReportBuyCard";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// ---------------------------------------------------------------------------
// WUA+ — dedicated marketing landing page.
// Structure (top → bottom):
//   1. Hero — "Editorial + AI + Human counsellor" reframe
//   2. "How it works" 4-stage journey
//   3. 3-tier pricing (Foundation / Engaged / Premium)
//   4. Specialist add-ons (Athletic / Ivy / Merit)
//   5. Specialist counsellors — regions × curriculums
//   6. Ready-to-begin CTA strip
//
// All CTAs funnel to /wua-plus (existing lead-capture form) with a
// `?intent=<tier>` query param so the sales team can triage. Copy is
// literal from the provided marketing pack; visual style is our own
// (light stone / editorial serif) — deliberately NOT the black-and-blue
// aesthetic of the source screenshots.
// ---------------------------------------------------------------------------

const STAGES = [
  {
    n: "01", label: "Stage One",
    title: "Build your Family Wallet",
    body: "Gather school reports, certificates and achievements into one secure, verified portfolio that grows with your child.",
    Icon: Wallet,
  },
  {
    n: "02", label: "Stage Two",
    title: "Learn with confidence",
    body: "Ask Anna, our AI Guidance, anything and explore the Resource Hub. Clear answers instead of hours of unreliable searching.",
    Icon: BookOpen,
  },
  {
    n: "03", label: "Stage Three",
    title: "Plan your future",
    body: "Subject choices, profile building and a balanced university shortlist, mapped years ahead with counsellor input.",
    Icon: Map,
  },
  {
    n: "04", label: "Stage Four",
    title: "Apply with confidence",
    body: "Essays, interviews, scholarships and submissions with your counsellor reviewing every step and documents shared one click at a time.",
    Icon: Send,
  },
];


const TIERS = [
  {
    key: "foundation",
    eyebrow: "Starting point · Digital",
    name: "Foundation",
    tagline: "The essential tools to begin the journey with clarity and structure, at a genuinely accessible price.",
    price: "From $120 / year",
    priceSub: "or $12.50 / mo · free for first 50–100 families",
    ctaLabel: "Start your Family Wallet",
    ctaTo: "/wua-plus?intent=foundation",
    ctaVariant: "outline",
    accent: "border-stone-300 bg-white",
    features: [
      "Family Wallet (10 verified documents)",
      "Anna — reactive AI guidance & Q&A",
      "Resource Hub, full access",
      "30-minute orientation call",
      "One-click sharing with partnered universities",
    ],
  },
  {
    key: "engaged",
    eyebrow: "Most chosen alongside a decision · Advisory",
    name: "Engaged",
    tagline: "Everything in Foundation, plus direct, personalised support for the moments that matter most.",
    price: "By enquiry",
    priceSub: "Annual subscription · bespoke quote",
    ctaLabel: "Talk to us",
    ctaTo: "/wua-plus?intent=engaged",
    ctaVariant: "primary",
    accent: "border-[#4C1D95]/40 bg-gradient-to-br from-[#F5F0FF] to-white shadow-lg",
    features: [
      "Everything in Foundation, plus:",
      "Morrisby + MyUniChoices career profiling",
      "Counsellor-led debrief and strategy session",
      "Proactive AI guidance: timeline, shortlist, drafts",
      "Premium resource library and templates",
      "Wallet expanded to 20 documents",
    ],
  },
  {
    key: "premium",
    eyebrow: "Concierge partnership · Dedicated",
    name: "Premium",
    tagline: "Everything in Engaged, plus a dedicated counsellor managing the entire journey alongside you.",
    price: "Bespoke quote",
    priceSub: "Priced per family · book a call and we'll scope it",
    ctaLabel: "Book a consultation",
    ctaTo: "/wua-plus?intent=premium",
    ctaVariant: "dark",
    accent: "border-stone-900 bg-stone-900 text-white",
    features: [
      "Everything in Engaged, plus:",
      "20 counsellor sessions across 24 months",
      "Full application support: UK, US, Europe",
      "Personal statement and essay coaching",
      "Monthly progress reports and decision support",
      "Wallet: 100 documents, unlimited verifications",
    ],
  },
];


const ADD_ONS = [
  {
    title: "Athletic Scholarship Pathway",
    label: "Two-tier add-on",
    body: "NCAA recruiting calendar, athletic profile development, eligibility centre support and coach outreach. From guidance-level to full agent-style recruiting support.",
    Icon: Trophy,
  },
  {
    title: "Ivy & Elite Focus",
    label: "6-session intensive",
    body: "Oxbridge and US Ivy League strategy intensive: target identification, profile gap analysis, supra-curricular shortlisting, ED/EA strategy, interview preparation and application narrative.",
    Icon: GraduationCap,
  },
  {
    title: "Merit Scholarship Advisory",
    label: "4-session package",
    body: "US merit scholarship strategy: landscape mapping, application strategy, optimisation of existing applications for scholarship review and follow-through guidance.",
    Icon: Landmark,
  },
];


const REGIONS = [
  "United Kingdom & Ireland",
  "United States & Canada",
  "Europe",
  "Middle East & North Africa",
  "Australia & New Zealand",
];


const CURRICULA = [
  { name: "International Baccalaureate", body: "HL, SL, theory of knowledge, extended essay" },
  { name: "British Curriculum", body: "A-Level, IGCSE, GCSE, subject strategy, predicted grades" },
  { name: "US High School + AP", body: "GPA, AP selection, SAT / ACT planning" },
  { name: "Indian curriculums", body: "CBSE, ICSE, state boards" },
  { name: "Local UAE / GCC", body: "MOE accreditation, regional considerations" },
];


// -------- helpers ---------------------------------------------------------
function TierButton({ tier }) {
  const base = "w-full mt-6 rounded-full font-medium";
  if (tier.ctaVariant === "primary")
    return (
      <Button asChild className={`${base} bg-[#4C1D95] hover:bg-[#3B0764] text-white`} data-testid={`plus-tier-cta-${tier.key}`}>
        <Link to={tier.ctaTo}>{tier.ctaLabel}</Link>
      </Button>
    );
  if (tier.ctaVariant === "dark")
    return (
      <Button asChild className={`${base} bg-white text-stone-900 hover:bg-stone-100`} data-testid={`plus-tier-cta-${tier.key}`}>
        <Link to={tier.ctaTo}>{tier.ctaLabel}</Link>
      </Button>
    );
  return (
    <Button asChild variant="outline" className={`${base} border-stone-800 hover:bg-stone-50`} data-testid={`plus-tier-cta-${tier.key}`}>
      <Link to={tier.ctaTo}>{tier.ctaLabel}</Link>
    </Button>
  );
}


/**
 * ReturningBuyerChip — a compact strip that appears at the very top of
 * `/plus` for signed-in users who have already bought at least one
 * report. Silent for guests and first-time visitors so the marketing
 * page reads unchanged for everyone else.
 */
function ReturningBuyerChip() {
  const { user, loading } = useAuth();
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (loading || !user) return;
    api.get("/wua-plus/reports")
      .then((r) => setCount(r.data?.count || 0))
      .catch(() => setCount(0));
  }, [loading, user]);
  if (!user || count === 0) return null;
  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-4">
      <Link
        to="/plus/my-reports"
        data-testid="plus-returning-buyer-chip"
        className="group inline-flex items-center gap-2 rounded-full border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-[12px] text-emerald-900 hover:bg-emerald-100 transition"
      >
        <FileText className="h-3.5 w-3.5" aria-hidden />
        You have {count} previous report{count === 1 ? "" : "s"}
        <span className="text-emerald-700 group-hover:underline font-medium">
          Open the list <ArrowRight className="inline h-3 w-3 ml-0.5" aria-hidden />
        </span>
      </Link>
    </div>
  );
}


export default function Plus() {
  return (
    <div data-testid="plus-page" className="bg-[#FDFBF7]">
      <PlusReportBuyCard />
      <ReturningBuyerChip />
      {/* ---- HERO --------------------------------------------------- */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-20">
        <img
          src="/logo-plus.png"
          alt="WhichUniversityAdvisor+"
          data-testid="plus-hero-logo"
          className="h-16 sm:h-20 w-auto mb-6"
        />
        <div className="inline-flex items-center gap-1.5 rounded-full border border-[#4C1D95]/30 bg-[#F5F0FF] px-3 py-1"
             data-testid="plus-eyebrow">
          <Sparkles className="h-3 w-3 text-[#4C1D95]" />
          <span className="label-uppercase !text-[10px] text-[#4C1D95]">Powered by Pearsana</span>
        </div>
        <h1 className="font-editorial text-4xl sm:text-5xl lg:text-6xl mt-4 leading-tight max-w-4xl">
          From editorial guidance, to <em>Anna</em>, to a counsellor who knows <em>you</em>.
        </h1>
        <p className="mt-5 text-lg text-stone-700 max-w-3xl leading-relaxed">
          The free WhichUniversityAdvisor is where every family should start. When you're ready for
          deeper, personalised support — proactive AI, a verified Family Wallet, and human counsellors
          matched to your destination and curriculum — <span className="font-semibold">WUA+</span>,
          powered by Pearsana, is <em>the</em> solution.
        </p>
        <div className="mt-8 flex items-center gap-3 flex-wrap">
          <Button asChild size="lg" className="bg-[#4C1D95] hover:bg-[#3B0764] text-white rounded-full px-6"
                  data-testid="plus-hero-cta-start">
            <Link to="/wua-plus?intent=foundation">Start your Family Wallet <ArrowRight className="h-4 w-4 ml-1.5" /></Link>
          </Button>
          <Button asChild size="lg" variant="outline" className="rounded-full px-6"
                  data-testid="plus-hero-cta-call">
            <Link to="/wua-plus?intent=engaged">Book a call</Link>
          </Button>
          <span className="text-xs text-stone-500 ml-1">
            Free to set up · no card required · free for our first 50–100 families
          </span>
        </div>
      </section>

      {/* ---- HOW IT WORKS ------------------------------------------ */}
      <section className="border-t border-stone-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="label-uppercase text-[#4C1D95]">A journey, not a transaction</div>
          <h2 className="font-editorial text-3xl sm:text-4xl mt-3 max-w-2xl">How it works.</h2>
          <p className="mt-3 text-stone-600 max-w-2xl">
            Most families only find support at the very end of the journey. WUA+ is built around
            four connected stages, so you have guidance from the first subject choice — not just the
            final deadline.
          </p>
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {STAGES.map((s) => (
              <Card key={s.n} data-testid={`plus-stage-${s.n}`}
                    className="p-6 border-stone-200 bg-white lift">
                <div className="h-10 w-10 rounded-full bg-[#4C1D95] text-white font-mono text-sm flex items-center justify-center">
                  {s.n}
                </div>
                <div className="label-uppercase !text-[10px] text-stone-500 mt-4">{s.label}</div>
                <h3 className="font-editorial text-xl mt-1 leading-tight">{s.title}</h3>
                <p className="text-sm text-stone-600 mt-3 leading-relaxed">{s.body}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ---- PRICING TIERS ----------------------------------------- */}
      <section className="border-t border-stone-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="label-uppercase text-[#4C1D95]">Three tiers, one journey</div>
          <h2 className="font-editorial text-3xl sm:text-4xl mt-3 max-w-2xl">Pick the level of support that fits today.</h2>
          <p className="mt-3 text-stone-600 max-w-2xl">
            You can move up a tier at any time — the work you've already done in your Family Wallet
            travels with you.
          </p>

          <div className="mt-10 grid md:grid-cols-3 gap-5 items-stretch">
            {TIERS.map((t) => {
              const isDark = t.ctaVariant === "dark";
              return (
                <Card key={t.key} data-testid={`plus-tier-${t.key}`}
                      className={`p-7 flex flex-col ${t.accent}`}>
                  <div className={`label-uppercase !text-[10px] ${isDark ? "text-stone-300" : "text-[#4C1D95]"}`}>
                    {t.eyebrow}
                  </div>
                  <h3 className={`font-editorial text-3xl mt-2 ${isDark ? "text-white" : "text-stone-900"}`}>{t.name}</h3>
                  <p className={`text-sm mt-3 leading-relaxed ${isDark ? "text-stone-300" : "text-stone-600"}`}>
                    {t.tagline}
                  </p>
                  <div className="mt-6">
                    <div className={`font-editorial text-2xl ${isDark ? "text-white" : "text-stone-900"}`}>{t.price}</div>
                    <div className={`text-[11px] font-underground-sc tracking-wider mt-1 ${isDark ? "text-stone-400" : "text-stone-500"}`}>
                      {t.priceSub}
                    </div>
                  </div>
                  <ul className={`mt-6 space-y-2.5 border-t ${isDark ? "border-stone-700" : "border-stone-200"} pt-5 flex-1`}>
                    {t.features.map((f) => (
                      <li key={f} className={`flex items-start gap-2 text-sm ${isDark ? "text-stone-200" : "text-stone-700"}`}>
                        <Check className={`h-3.5 w-3.5 mt-1 shrink-0 ${isDark ? "text-white" : "text-[#4C1D95]"}`} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <TierButton tier={t} />
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* ---- SPECIALIST ADD-ONS ------------------------------------ */}
      <section className="border-t border-stone-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="label-uppercase text-[#4C1D95]">Specialist add-ons</div>
          <h2 className="font-editorial text-3xl sm:text-4xl mt-3 max-w-2xl">Targeted support for specific pathways.</h2>
          <p className="mt-3 text-stone-600 max-w-2xl">
            Some students need expert support beyond the standard package. Available alongside any level.
          </p>
          <div className="mt-10 grid md:grid-cols-3 gap-5">
            {ADD_ONS.map((a) => (
              <Card key={a.title} data-testid={`plus-addon-${a.title.split(" ")[0].toLowerCase()}`}
                    className="p-6 border-stone-200 bg-white lift border-l-4 border-l-[#4C1D95]">
                <a.Icon className="h-6 w-6 text-[#4C1D95]" />
                <h3 className="font-editorial text-xl mt-3 leading-tight">{a.title}</h3>
                <p className="text-sm text-stone-600 mt-3 leading-relaxed">{a.body}</p>
                <span className="mt-4 inline-block text-[10px] font-underground-sc tracking-wider bg-[#F5F0FF] text-[#4C1D95] rounded-full px-2 py-1">
                  {a.label}
                </span>
              </Card>
            ))}
          </div>
          <p className="mt-8 text-sm text-stone-600">
            Plus <span className="font-semibold">Essay Intensive</span>,{" "}
            <span className="font-semibold">SAT &amp; ACT prep</span> and{" "}
            <span className="font-semibold">specialist subject tutoring</span>, explored during onboarding.
          </p>
        </div>
      </section>

      {/* ---- COUNSELLORS ------------------------------------------- */}
      <section className="border-t border-stone-200 bg-[#F5F0FF]/40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="label-uppercase text-[#4C1D95]">Specialist counsellors</div>
          <h2 className="font-editorial text-3xl sm:text-4xl mt-3 max-w-2xl">Experienced advisors for every pathway.</h2>
          <p className="mt-3 text-stone-600 max-w-2xl">
            Counsellors are matched to your family's destination and curriculum — and your family
            stays with the same counsellor throughout the journey.
          </p>
          <div className="mt-10 grid md:grid-cols-2 gap-5">
            <Card className="p-6 border-stone-200 bg-white" data-testid="plus-regions">
              <div className="flex items-center gap-2 pb-3 border-b border-stone-200">
                <Globe className="h-4 w-4 text-[#4C1D95]" />
                <div className="label-uppercase !text-[10px]">Regions we cover</div>
              </div>
              <ul className="mt-3 divide-y divide-stone-100">
                {REGIONS.map((r) => (
                  <li key={r} className="py-2.5 text-sm font-medium text-stone-800">{r}</li>
                ))}
              </ul>
            </Card>
            <Card className="p-6 border-stone-200 bg-white" data-testid="plus-curricula">
              <div className="flex items-center gap-2 pb-3 border-b border-stone-200">
                <ScrollText className="h-4 w-4 text-[#4C1D95]" />
                <div className="label-uppercase !text-[10px]">Curriculums we know</div>
              </div>
              <ul className="mt-3 divide-y divide-stone-100">
                {CURRICULA.map((c) => (
                  <li key={c.name} className="py-2.5">
                    <div className="text-sm font-medium text-stone-900">{c.name}</div>
                    <div className="text-[11px] text-stone-500 mt-0.5">{c.body}</div>
                  </li>
                ))}
              </ul>
            </Card>
          </div>
        </div>
      </section>

      {/* ---- READY TO BEGIN ---------------------------------------- */}
      <section className="border-t border-stone-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="inline-flex items-center gap-1.5 rounded-full border border-[#4C1D95]/30 bg-[#F5F0FF] px-3 py-1">
            <Sparkles className="h-3 w-3 text-[#4C1D95]" />
            <span className="label-uppercase !text-[10px] text-[#4C1D95]">Ready to begin?</span>
          </div>
          <h2 className="font-editorial text-4xl sm:text-5xl mt-4 max-w-3xl leading-tight">
            Start your family's journey today.
          </h2>
          <p className="mt-4 text-stone-600 max-w-2xl">
            Free to set up. No card required. There is no single "right" way to start; wherever you're
            beginning from, we're glad you're here.
          </p>
          <div className="mt-8 flex items-center gap-3 flex-wrap">
            <Button asChild size="lg"
                    className="bg-[#4C1D95] hover:bg-[#3B0764] text-white rounded-full px-6"
                    data-testid="plus-ready-cta-start">
              <Link to="/wua-plus?intent=foundation">Start your Family Wallet</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-full px-6"
                    data-testid="plus-ready-cta-call">
              <Link to="/wua-plus?intent=engaged">Book a call</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
